import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, BadgeCheck, ZoomIn } from "lucide-react";
import { CERTIFICATIONS } from "../lib/constants";
import useReveal from "../hooks/useReveal";

export default function Certifications() {
  const head = useReveal();
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const close = useCallback(() => setLightboxIdx(null), []);
  const next = useCallback(
    () =>
      setLightboxIdx((i) =>
        i === null ? i : (i + 1) % CERTIFICATIONS.length
      ),
    []
  );
  const prev = useCallback(
    () =>
      setLightboxIdx((i) =>
        i === null ? i : (i - 1 + CERTIFICATIONS.length) % CERTIFICATIONS.length
      ),
    []
  );

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lightboxIdx, close, next, prev]);

  return (
    <section
      id="certifications"
      data-testid="certifications-section"
      className="py-16 md:py-20 bg-white"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={head.ref}
          className={`fade-in-up ${head.visible ? "is-visible" : ""} text-center max-w-2xl mx-auto`}
        >
          <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-[#00B4D8]">
            <BadgeCheck className="w-4 h-4" />
            Credentials
          </span>
          <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] tracking-tight">
            We&apos;re Certified
          </h2>
          <p className="mt-4 text-base lg:text-lg text-slate-500 leading-relaxed">
            Our certifications demonstrate our professional training and
            commitment to providing safe, reliable, and high-quality solar
            panel services.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-6">
          {CERTIFICATIONS.map((cert, i) => (
            <CertCard
              key={i}
              cert={cert}
              index={i}
              onOpen={() => setLightboxIdx(i)}
            />
          ))}
        </div>
      </div>

      {lightboxIdx !== null && (
        <Lightbox
          cert={CERTIFICATIONS[lightboxIdx]}
          index={lightboxIdx}
          total={CERTIFICATIONS.length}
          onClose={close}
          onNext={next}
          onPrev={prev}
        />
      )}
    </section>
  );
}

function CertCard({ cert, index, onOpen }) {
  const { ref, visible } = useReveal();
  return (
    <button
      type="button"
      ref={ref}
      data-testid={`cert-card-${index}`}
      onClick={onOpen}
      className={`fade-in-up ${visible ? "is-visible" : ""} group bg-white border border-slate-200 rounded-2xl p-4 shadow-sm hover:shadow-xl hover:shadow-[#0A192F]/8 hover:border-[#00B4D8]/40 transition-all duration-500 hover:-translate-y-1 text-left`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="relative overflow-hidden rounded-xl bg-slate-50">
        <div className="aspect-[4/3] flex items-center justify-center p-2">
          <img
            src={cert.image}
            alt={cert.name}
            loading="lazy"
            className="max-w-full max-h-full w-auto h-auto object-contain transition-transform duration-700 group-hover:scale-[1.03]"
          />
        </div>
        <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur border border-slate-200 flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all shadow-sm">
          <ZoomIn className="w-4 h-4 text-[#0A192F]" />
        </div>
      </div>
      <div className="mt-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-heading font-semibold text-[#0A192F] text-sm leading-snug truncate">
            {cert.issuer}
          </p>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
            {cert.name}
          </p>
        </div>
      </div>
      <p
        data-testid={`cert-caption-${index}`}
        className="mt-3 text-[11px] font-semibold tracking-[0.15em] uppercase text-[#00B4D8]"
      >
        Click to enlarge
      </p>
    </button>
  );
}

function Lightbox({ cert, index, total, onClose, onNext, onPrev }) {
  return (
    <div
      data-testid="cert-lightbox"
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
    >
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 sm:p-6 z-10">
        <div className="text-white">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00B4D8]">
            Certification
          </p>
          <p className="text-sm text-white/80 mt-0.5 max-w-md truncate">
            {cert.issuer} · {index + 1} of {total}
          </p>
        </div>
        <button
          type="button"
          data-testid="cert-lightbox-close"
          onClick={onClose}
          aria-label="Close"
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <img
        src={cert.image}
        alt={cert.name}
        className="max-w-[95vw] max-h-[85vh] object-contain rounded-xl shadow-2xl bg-white"
        onClick={(e) => e.stopPropagation()}
      />

      {total > 1 && (
        <>
          <button
            type="button"
            data-testid="cert-lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            aria-label="Previous"
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <button
            type="button"
            data-testid="cert-lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            aria-label="Next"
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white transition-colors"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </>
      )}
    </div>
  );
}
